import React from "react";
import { Box, ButtonBase } from "@mui/material";
import { Theme } from "@mui/material/styles";

export type KpiTone = "brand" | "ok" | "warn" | "err";

export interface KpiTileProps {
  /** The number, already formatted (e.g. formatMoney(total)). Use "—" while loading. */
  value: React.ReactNode;
  /** What the number is, e.g. "Distribution total". */
  label: React.ReactNode;
  /** Small line under the label, e.g. "312 bills". */
  sub?: React.ReactNode;
  /** Colour of the left rule and the value. Default 'brand'. */
  tone?: KpiTone;
  /** Makes the tile a toggle button (e.g. to filter a list). */
  onClick?: () => void;
  /** Pressed state when the tile is a toggle. */
  selected?: boolean;
}

const toneColor = (theme: Theme, tone: KpiTone) =>
  tone === "brand" ? theme.palette.primary.main : theme.palette.status[tone].fg;

/**
 * Headline number with a 4px left rule in its tone (AB#6872 dashboard tiles).
 * Becomes an accessible toggle button when `onClick` is given.
 */
export function KpiTile({
  value,
  label,
  sub,
  tone = "brand",
  onClick,
  selected = false,
}: KpiTileProps) {
  const content = (
    <>
      <Box
        component="span"
        sx={(theme) => ({
          display: "block",
          fontSize: "1.5rem",
          fontWeight: 500,
          lineHeight: 1.3,
          fontVariantNumeric: "tabular-nums",
          color: toneColor(theme, tone),
        })}
      >
        {value}
      </Box>
      <Box
        component="span"
        sx={{
          display: "block",
          fontSize: "0.78125rem",
          color: "text.secondary",
        }}
      >
        {label}
      </Box>
      {sub && (
        <Box
          component="span"
          sx={{
            display: "block",
            mt: 0.5,
            fontSize: "0.71875rem",
            color: "text.secondary",
          }}
        >
          {sub}
        </Box>
      )}
    </>
  );

  const tileSx = (theme: Theme) => {
    const color = toneColor(theme, tone);
    return {
      // Flex column so a <button> tile (clickable) lays out like a plain one.
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "flex-start",
      width: "100%",
      textAlign: "center" as const,
      padding: "14px 16px",
      backgroundColor: selected
        ? theme.palette.brandTint
        : theme.palette.background.paper,
      border: `1px solid ${selected ? color : theme.palette.divider}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: "6px",
      boxShadow: selected ? `0 0 0 1px ${color}` : "none",
    };
  };

  if (onClick) {
    return (
      <ButtonBase
        onClick={onClick}
        aria-pressed={selected}
        sx={(theme) => ({
          ...tileSx(theme),
          font: "inherit",
          "&:hover": {
            backgroundColor: selected
              ? theme.palette.brandTint
              : theme.palette.grey[50],
          },
        })}
      >
        {content}
      </ButtonBase>
    );
  }

  return <Box sx={tileSx}>{content}</Box>;
}

export default KpiTile;
